export type PromptPlanDomain =
  | 'booking'
  | 'clinical-intake'
  | 'field-inspection'
  | 'financial-reconciliation'
  | 'onboarding'
  | 'procurement'
  | 'realtime-chat'
  | 'reporting'
  | 'task-tracking'

interface DomainRule {
  id: PromptPlanDomain
  primary: boolean
  patterns: RegExp[]
}

const DOMAIN_RULES: DomainRule[] = [
  {
    id: 'financial-reconciliation',
    primary: true,
    patterns: [
      /\brevenue\b/i,
      /\bvariance\b/i,
      /\breconciliation\b/i,
      /\bsettlement\b/i,
      /\bledger\b/i,
      /\binvoice\b/i,
      /\bbilling\b/i,
      /(?:매출|수익|대사|정산|차이|청구)/,
    ],
  },
  {
    id: 'procurement',
    primary: true,
    patterns: [
      /\bprocurement\b/i,
      /\bpurchase\s+orders?\b/i,
      /\bsuppliers?\b/i,
      /\bvendors?\b/i,
      /(?:구매|조달|발주|공급업체|납기|재고)/,
    ],
  },
  {
    id: 'clinical-intake',
    primary: true,
    patterns: [
      /\bclinical\b/i,
      /\bclinic\b/i,
      /\bpatients?\b/i,
      /\breferrals?\b/i,
      /\btriage\b/i,
      /(?:임상|병원|환자|의뢰|진료|트리아지)/,
    ],
  },
  {
    id: 'field-inspection',
    primary: true,
    patterns: [
      /\binspections?\b/i,
      /\bsite\s+visits?\b/i,
      /\bphoto\s+evidence\b/i,
      /\boffline\s+capture\b/i,
      /\bsync\s+conflicts?\b/i,
      /(?:현장|검사|점검|사진\s*증거|오프라인|동기화\s*충돌)/,
    ],
  },
  {
    id: 'booking',
    primary: true,
    patterns: [
      /\bbookings?\b/i,
      /\breservations?\b/i,
      /\bappointments?\b/i,
      /\bavailability\b/i,
      /(?:예약|일정|가용|약속)/,
    ],
  },
  {
    id: 'realtime-chat',
    primary: true,
    patterns: [
      /\breal[ -]?time\s+chat\b/i,
      /\breal[ -]?time\s+messages?\b/i,
      /\bchat\b/i,
      /\bmessages?\s+(?:composer|delivery|list|thread)\b/i,
      /\bconversation\b/i,
      /\binbox\b/i,
      /(?:실시간\s*채팅|채팅|메시지|대화|받은\s*편지함)/,
    ],
  },
  {
    id: 'onboarding',
    primary: true,
    patterns: [
      /\bonboarding\b/i,
      /\bsign[ -]?up\b/i,
      /\bprofile\s+setup\b/i,
      /\bmulti[ -]?step\s+wizard\b/i,
      /(?:온보딩|가입|프로필\s*설정|단계별)/,
    ],
  },
  {
    id: 'task-tracking',
    primary: true,
    patterns: [
      /\btask[\s_-]+tracker\b/i,
      /\btask[\s_-]+list\b/i,
      /\bto-?do\b/i,
      /\bchecklist\b/i,
      /(?:할\s*일|작업\s*추적|체크리스트)/,
    ],
  },
  {
    id: 'reporting',
    primary: false,
    patterns: [
      /\breports?\b/i,
      /\banalytics\b/i,
      /\bexports?\b/i,
      /\bcsv\b/i,
      /\bexcel\b/i,
      /\bpdf\b/i,
      /(?:보고서|분석|내보내기|엑셀|피디에프)/,
    ],
  },
]

export interface PromptPlanFidelityInput {
  prompt: string
  blocks: Array<{
    name?: string
    type?: string
    description?: string
    binds?: string[]
  }>
  globalState?: Record<string, unknown>
}

export interface PromptPlanFidelityEvidence {
  schemaVersion: 'studio-prompt-plan-fidelity.v1'
  admitted: boolean
  requiredDomains: PromptPlanDomain[]
  plannedDomains: PromptPlanDomain[]
  missingDomains: PromptPlanDomain[]
  conflictingDomains: PromptPlanDomain[]
  promptAnchors: string[]
  matchedAnchors: string[]
}

const GENERIC_PROMPT_TERMS = new Set([
  'a',
  'an',
  'and',
  'app',
  'application',
  'build',
  'create',
  'for',
  'make',
  'new',
  'the',
  'use',
  'with',
  'workspace',
  '만들어',
  '만들기',
  '만든다',
  '앱',
  '어플',
  '애플리케이션',
  '위한',
  '사용',
])

function normalizeAnchor(token: string): string {
  const lower = token.toLowerCase()
  if (/^[a-z0-9]+$/.test(lower)) {
    if (lower.length > 5 && lower.endsWith('ies')) return `${lower.slice(0, -3)}y`
    if (lower.length > 5 && lower.endsWith('ing')) return lower.slice(0, -3)
    if (lower.length > 4 && lower.endsWith('ed')) return lower.slice(0, -2)
    if (lower.length > 4 && lower.endsWith('s')) return lower.slice(0, -1)
  }
  return lower
}

function semanticAnchors(text: string): string[] {
  const tokens = text
    .replace(/[_-]+/g, ' ')
    .match(/[\p{L}\p{N}]+/gu) ?? []
  return [...new Set(tokens
    .map(normalizeAnchor)
    .filter((token) => {
      if (GENERIC_PROMPT_TERMS.has(token)) return false
      return /^[a-z0-9]+$/.test(token) ? token.length >= 3 : token.length >= 2
    }))]
}

function anchorsMatch(promptAnchor: string, planAnchor: string): boolean {
  if (promptAnchor === planAnchor) return true
  if (promptAnchor.length < 5 || planAnchor.length < 5) return false
  return promptAnchor.startsWith(planAnchor) || planAnchor.startsWith(promptAnchor)
}

function domainsIn(text: string): PromptPlanDomain[] {
  return DOMAIN_RULES.filter((rule) =>
    rule.patterns.some((pattern) => pattern.test(text)),
  ).map((rule) => rule.id)
}

function primaryDomains(domains: PromptPlanDomain[]): PromptPlanDomain[] {
  const domainSet = new Set(domains)
  return DOMAIN_RULES.filter(
    (rule) => rule.primary && domainSet.has(rule.id),
  ).map((rule) => rule.id)
}

export function evaluatePromptPlanFidelity(
  input: PromptPlanFidelityInput,
): PromptPlanFidelityEvidence {
  const promptDomains = domainsIn(input.prompt)
  const promptPrimaryDomains = primaryDomains(promptDomains)
  const requiredDomains = promptDomains
  const planCorpus = [
    ...input.blocks.flatMap((block) => [
      block.name ?? '',
      block.type ?? '',
      block.description ?? '',
      ...(block.binds ?? []),
    ]),
    ...Object.keys(input.globalState ?? {}),
  ].join('\n')
  const plannedDomains = domainsIn(planCorpus)
  const promptAnchors = semanticAnchors(input.prompt)
  const planAnchors = semanticAnchors(planCorpus)
  const matchedAnchors = promptAnchors.filter((promptAnchor) =>
    planAnchors.some((planAnchor) => anchorsMatch(promptAnchor, planAnchor)),
  )
  const missingDomains = requiredDomains.filter(
    (domain) => !plannedDomains.includes(domain),
  )
  const conflictingDomains = primaryDomains(plannedDomains).filter(
    (domain) => !promptPrimaryDomains.includes(domain),
  )
  const minimumAnchorMatches = Math.min(2, promptAnchors.length)
  const unclassifiedPromptPreserved =
    requiredDomains.length > 0 ||
    (promptAnchors.length > 0 && matchedAnchors.length >= minimumAnchorMatches)

  return {
    schemaVersion: 'studio-prompt-plan-fidelity.v1',
    admitted:
      missingDomains.length === 0 &&
      conflictingDomains.length === 0 &&
      unclassifiedPromptPreserved,
    requiredDomains,
    plannedDomains,
    missingDomains,
    conflictingDomains,
    promptAnchors,
    matchedAnchors,
  }
}
